import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { useStationMode } from '@hooks/station';
import { shouldHideSidebarAndHeader } from '../../../router/Router';

const SIDEBAR_WIDTH = 215;
const PLAYLIST_QUEUE_WIDTH = 384;
const SM_BREAKPOINT = 640;
const CONTAINER_WIDTH = 160;

export interface ContentBounds {
  left: number;
  right: number;
  centerX: number;
}

export const useContentBounds = () => {
  const { pathname } = useLocation();
  const { playlistOpen } = useCommonState();
  const { isSessionPage } = useSessionFeatureState();
  const isStationMode = useStationMode();

  const computeBounds = useCallback((): ContentBounds => {
    const vw = window.innerWidth;
    const isMobile = vw < SM_BREAKPOINT;

    if (isSessionPage) {
      return {
        left: 0,
        right: vw,
        centerX: vw / 2 - CONTAINER_WIDTH / 2,
      };
    }

    const hideSidebar =
      shouldHideSidebarAndHeader(pathname) || isStationMode;
    const leftOffset = !isMobile && !hideSidebar ? SIDEBAR_WIDTH : 0;

    // Check if the queue panel is actually rendered and visible in the viewport,
    // not just open in Redux state (Musicplayer returns null when no active playlist)
    let rightOffset = 0;
    if (!isMobile && playlistOpen && !isStationMode) {
      const queueEl = document.querySelector<HTMLElement>(
        '[data-playlist-queue]',
      );
      if (queueEl) {
        const rect = queueEl.getBoundingClientRect();
        if (rect.left < vw) {
          rightOffset = PLAYLIST_QUEUE_WIDTH;
        }
      }
    }

    const contentLeft = leftOffset;
    const contentRight = vw - rightOffset;
    const contentWidth = contentRight - contentLeft;

    return {
      left: contentLeft,
      right: contentRight,
      centerX: contentLeft + contentWidth / 2 - CONTAINER_WIDTH / 2,
    };
  }, [isSessionPage, pathname, isStationMode, playlistOpen]);

  const [bounds, setBounds] = useState<ContentBounds>(computeBounds);

  useEffect(() => {
    setBounds(computeBounds());
  }, [computeBounds]);

  useEffect(() => {
    const handleResize = () => setBounds(computeBounds());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [computeBounds]);

  return bounds;
};
